import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { API_BASE_URL } from "../api/apiConfig";
import AdminShell from "../components/AdminShell";
import "../styles/admin.css";

const PAGE_SIZE = 20;

const roleOptions = [
  { value: "", label: "All roles" },
  { value: "technical_admin", label: "Technical Admin" },
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "user", label: "Customer" },
];

function formatDateTime(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRole(role) {
  const normalized = String(role || "").trim().toLowerCase();
  if (["user", "customer", "client"].includes(normalized)) return "Customer";
  return String(role || "system")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function titleCase(value) {
  return String(value || "N/A")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function prettyMetadata(metadata) {
  if (!metadata || typeof metadata !== "object") return "{}";
  return JSON.stringify(metadata, null, 2);
}

function getDeviceType(log = {}) {
  return ["Desktop", "Mobile", "Tablet", "Unknown"].includes(log.deviceType)
    ? log.deviceType
    : "Unknown";
}

function getDeviceLocation(log = {}) {
  if (typeof log.deviceLocation === "string" && log.deviceLocation.trim()) {
    return log.deviceLocation.trim();
  }

  if (log.deviceLocation && typeof log.deviceLocation === "object") {
    const parts = [
      log.deviceLocation.city,
      log.deviceLocation.region,
      log.deviceLocation.country,
    ].map((part) => (typeof part === "string" ? part.trim() : "")).filter(Boolean);
    if (parts.length) return parts.join(", ");
  }

  return "Not available";
}

function todayFilenameDate() {
  return new Date().toISOString().slice(0, 10);
}

function buildApiUrl(path, params = {}) {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (String(value || "").trim()) url.searchParams.set(key, String(value).trim());
  });
  return url.toString();
}

async function getExportErrorMessage(err, fallback) {
  const data = err?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (text) {
        const parsed = JSON.parse(text);
        if (parsed?.message) return parsed.message;
      }
    } catch (_) {
      // Fall through to the generic message if the blob is not JSON.
    }
  }

  return err?.response?.data?.message || fallback;
}

export default function AuditTrails() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({
    totalLogs: 0,
    todayActivities: 0,
    adminActions: 0,
    securityEvents: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    search: "",
    module: "",
    action: "",
    actorRole: "",
    dateFrom: "",
    dateTo: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportPdfError, setExportPdfError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  const queryParams = useMemo(() => {
    const params = {
      page: pagination.page,
      limit: PAGE_SIZE,
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (String(value || "").trim()) params[key] = String(value).trim();
    });

    return params;
  }, [filters, pagination.page]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/admin/audit-logs", { params: queryParams });
      setLogs(res.data.logs || []);
      setSummary((current) => ({ ...current, ...(res.data.summary || {}) }));
      setPagination((current) => ({
        ...current,
        ...(res.data.pagination || {}),
      }));
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load audit trails.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const moduleOptions = useMemo(() => {
    const values = new Set(logs.map((log) => log.module).filter(Boolean));
    ["Authentication", "Bookings", "Payments", "Users", "Vehicles", "Drivers", "Payment Methods", "Settings", "Marketing", "Restricted Areas"].forEach((item) => values.add(item));
    return ["", ...Array.from(values).sort()];
  }, [logs]);

  const actionOptions = useMemo(() => {
    const values = new Set(logs.map((log) => log.action).filter(Boolean));
    return ["", ...Array.from(values).sort()];
  }, [logs]);

  const updateFilter = (key, value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters({
      search: "",
      module: "",
      action: "",
      actorRole: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const exportParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (String(value || "").trim()) params[key] = String(value).trim();
    });
    return params;
  }, [filters]);

  const pdfExportParams = useMemo(() => {
    const params = {};
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.module.trim()) params.module = filters.module.trim();
    if (filters.action.trim()) params.action = filters.action.trim();
    if (filters.actorRole.trim()) params.role = filters.actorRole.trim();
    if (filters.dateFrom.trim()) params.startDate = filters.dateFrom.trim();
    if (filters.dateTo.trim()) params.endDate = filters.dateTo.trim();
    return params;
  }, [filters]);

  const exportCsv = async () => {
    setExporting(true);
    setExportError("");

    try {
      const res = await api.get("/admin/audit-logs/export.csv", {
        params: exportParams,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fleetx-audit-trails-${todayFilenameDate()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(
        err.response?.data?.message ||
          "Unable to export audit trails. Please try again."
      );
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    setExportPdfError("");
    const pdfExportPath = "/audit-trails/export/pdf";
    const finalUrl = buildApiUrl(pdfExportPath, pdfExportParams);
    console.log("Audit PDF export URL:", finalUrl);
    console.log("[AuditTrails] PDF export request start", { filters: pdfExportParams });

    try {
      const res = await api.get(pdfExportPath, {
        params: pdfExportParams,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "fleetx-audit-trails-report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      console.log("[AuditTrails] PDF export success", {
        bytes: res.data?.size || res.data?.byteLength || 0,
      });
    } catch (err) {
      console.error("[AuditTrails] PDF export failed", {
        status: err?.response?.status,
        message: err?.message || "Unknown export error",
      });
      setExportPdfError(
        await getExportErrorMessage(
          err,
          "Unable to export audit trails PDF. Please try again."
        )
      );
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <AdminShell
      pageClassName="admin-shell--audit-trails"
      title="Audit Trails"
      subtitle="Track administrator and system activities with timestamps for accountability."
      eyebrow="SETTINGS"
      actions={
        <>
          <button
            type="button"
            className="btn btn-soft audit-header-action"
            onClick={exportCsv}
            disabled={exporting}
          >
            {exporting ? "Exporting CSV..." : "Export CSV"}
          </button>
          <button
            type="button"
            className="btn btn-soft audit-header-action"
            onClick={exportPdf}
            disabled={exportingPdf}
          >
            {exportingPdf ? "Exporting PDF..." : "Export PDF"}
          </button>
          <button type="button" className="btn btn-primary audit-header-action audit-header-action--primary" onClick={fetchLogs} disabled={loading}>
            Refresh
          </button>
        </>
      }
    >
      <div className="audit-page">
        <section className="audit-summary-grid">
          <article className="audit-summary-card audit-summary-card--total">
            <span className="audit-summary-card__icon" aria-hidden="true">▣</span>
            <div><span>Total logs</span>
            <strong>{summary.totalLogs || pagination.total || 0}</strong></div>
          </article>
          <article className="audit-summary-card audit-summary-card--today">
            <span className="audit-summary-card__icon" aria-hidden="true">◷</span>
            <div><span>Today's activities</span>
            <strong>{summary.todayActivities || 0}</strong></div>
          </article>
          <article className="audit-summary-card audit-summary-card--admin">
            <span className="audit-summary-card__icon" aria-hidden="true">●</span>
            <div><span>Admin actions</span>
            <strong>{summary.adminActions || 0}</strong></div>
          </article>
          <article className="audit-summary-card audit-summary-card--security">
            <span className="audit-summary-card__icon" aria-hidden="true">◇</span>
            <div><span>Security events</span>
            <strong>{summary.securityEvents || 0}</strong></div>
          </article>
        </section>

        <section className="audit-panel">
          <div className="audit-panel__head">
            <div>
              <h2>Activity Records</h2>
              <p>Audit logs are system records. They can be exported for reporting but cannot be edited or deleted.</p>
            </div>
            {hasFilters ? (
              <button type="button" className="btn btn-soft" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          </div>

          <div className="audit-filters">
            <input
              className="input"
              placeholder="Search actor, action, or description"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
            />
            <select className="select" value={filters.module} onChange={(event) => updateFilter("module", event.target.value)}>
              {moduleOptions.map((item) => (
                <option key={item || "all-modules"} value={item}>
                  {item || "All modules"}
                </option>
              ))}
            </select>
            <select className="select" value={filters.action} onChange={(event) => updateFilter("action", event.target.value)}>
              {actionOptions.map((item) => (
                <option key={item || "all-actions"} value={item}>
                  {item ? titleCase(item) : "All actions"}
                </option>
              ))}
            </select>
            <select className="select" value={filters.actorRole} onChange={(event) => updateFilter("actorRole", event.target.value)}>
              {roleOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <input className="input" type="date" value={filters.dateFrom} onChange={(event) => updateFilter("dateFrom", event.target.value)} />
            <input className="input" type="date" value={filters.dateTo} onChange={(event) => updateFilter("dateTo", event.target.value)} />
          </div>

          {error ? (
            <div className="audit-error">
              <span>{error}</span>
              <button type="button" onClick={fetchLogs}>Retry</button>
            </div>
          ) : null}

          {exportError ? (
            <div className="audit-error">
              <span>{exportError}</span>
              <button type="button" onClick={exportCsv} disabled={exporting}>
                Retry CSV export
              </button>
            </div>
          ) : null}

          {exportPdfError ? (
            <div className="audit-error">
              <span>{exportPdfError}</span>
              <button type="button" onClick={exportPdf} disabled={exportingPdf}>
                Retry PDF export
              </button>
            </div>
          ) : null}

          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Role</th>
                  <th>Module</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th>Target</th>
                  <th>IP Address</th>
                  <th>Device</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td colSpan="10"><span className="audit-skeleton" /></td>
                    </tr>
                  ))
                ) : logs.length ? (
                  logs.map((log) => (
                    <tr key={log._id} onClick={() => setSelectedLog(log)}>
                      <td>{formatDateTime(log.createdAt)}</td>
                      <td>
                        <strong>{log.actorName || "System"}</strong>
                        <small>{log.actorEmail || "N/A"}</small>
                      </td>
                      <td><span className="audit-badge" data-role={log.actorRole}>{formatRole(log.actorRole)}</span></td>
                      <td>{log.module || "N/A"}</td>
                      <td>{titleCase(log.action)}</td>
                      <td>{log.description || "N/A"}</td>
                      <td>{log.targetLabel || log.targetId || log.targetType || "N/A"}</td>
                      <td>{log.ipAddress || "N/A"}</td>
                      <td className="audit-device-cell">
                        <strong>{getDeviceType(log)}</strong>
                        <small>{getDeviceLocation(log)}</small>
                      </td>
                      <td>
                        <button type="button" className="btn btn-soft audit-view-btn" onClick={(event) => { event.stopPropagation(); setSelectedLog(log); }}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="audit-empty">No audit activities recorded yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="audit-pagination">
            <span>
              Showing {logs.length} of {pagination.total || 0} logs
            </span>
            <div>
              <button
                type="button"
                className="btn btn-soft"
                disabled={loading || pagination.page <= 1}
                onClick={() => setPagination((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
              >
                Previous
              </button>
              <strong>Page {pagination.page} of {pagination.totalPages || 1}</strong>
              <button
                type="button"
                className="btn btn-soft"
                disabled={loading || pagination.page >= pagination.totalPages}
                onClick={() => setPagination((current) => ({ ...current, page: current.page + 1 }))}
              >
                Next
              </button>
            </div>
          </div>
        </section>

        {selectedLog ? (
          <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
            <div className="audit-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
              <div className="audit-modal__head">
                <div>
                  <span>Audit Record</span>
                  <h2>{titleCase(selectedLog.action)}</h2>
                </div>
                <button type="button" className="btn-close" onClick={() => setSelectedLog(null)}>Close</button>
              </div>
              <div className="audit-detail-grid">
                <div><span>Actor</span><strong>{selectedLog.actorName || "System"}</strong><small>{selectedLog.actorEmail || "N/A"}</small></div>
                <div><span>Role</span><strong>{formatRole(selectedLog.actorRole)}</strong></div>
                <div><span>Module</span><strong>{selectedLog.module || "N/A"}</strong></div>
                <div><span>Timestamp</span><strong>{formatDateTime(selectedLog.createdAt)}</strong></div>
                <div><span>Target</span><strong>{selectedLog.targetLabel || selectedLog.targetId || selectedLog.targetType || "N/A"}</strong></div>
                <div><span>IP Address</span><strong>{selectedLog.ipAddress || "N/A"}</strong></div>
                <div><span>Device Type</span><strong>{getDeviceType(selectedLog)}</strong></div>
                <div><span>Device Location</span><strong>{getDeviceLocation(selectedLog)}</strong></div>
              </div>
              <section className="audit-detail-section">
                <span>Description</span>
                <p>{selectedLog.description || "N/A"}</p>
              </section>
              <section className="audit-detail-section">
                <span>User Agent</span>
                <p>{selectedLog.userAgent || "N/A"}</p>
              </section>
              <section className="audit-detail-section">
                <span>Metadata</span>
                <pre>{prettyMetadata(selectedLog.metadata)}</pre>
              </section>
            </div>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
